import { 
  Box, 
  Button, 
  Flex, 
  Grid, 
  Heading, 
  Icon, 
  Image, 
  Link, 
  Text, 
  useMediaQuery 
} from '@chakra-ui/react';
import './style.css';

interface MarketplaceTileProps {
  href: string;
  title: string;
  imageSrc: string;
  isEmpty?: boolean;
}

interface FeatureTileProps {
  imageSrc: string;
  alt: string;
}

interface IntegrationData {
  title: string;
  subtitle: string;
  description: string;
  items: Array<{
    title: string;
    imageSrc: string;
    href?: string;
  }>;
}

interface FeatureListItemProps {
  title: string;
  description: string;
}

// Components
const MarketplaceTile: React.FC<MarketplaceTileProps> = ({ href, title, imageSrc, isEmpty = false }) => (
  <Link 
    href={href}
    display="flex"
    justifyContent="center"
    alignItems="center"
    position="relative"
    aspectRatio="1/1"
    p="10px"
    bg={isEmpty ? 'white' : '#eee'}
    border={isEmpty ? '2px dashed var(--color-border-gray)' : '1px solid #eee'}
    _hover={{ bg: '#fff' }}
  >
    <Flex direction="column" align="center" justify="center" gap="10px">
      <Image src={imageSrc} alt={title} />
      {isEmpty && (
        <Text textAlign="center" fontSize="13.764px" fontWeight="600">
          {title}
                    </Text>
      )}
    </Flex>
    <Box position="absolute" bottom="10px" right="10px" w="14px" h="14px">
      <Image src="https://www.idosell.com/panel/file-gate.php?id=596524&ext=.svg" alt="icon" />
    </Box>
  </Link>
);

const FeatureTile: React.FC<FeatureTileProps> = ({ imageSrc, alt }) => (
  <Box
    bg={{base: "#E3E3E3", md: "#fff"}}
    aspectRatio={{base: "1/1", md: "unset"}}
    p="20px"
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
  > 
    <Image src={imageSrc} alt={alt} />
  </Box>
);

const CustomSection: React.FC<{
  imageUrl: string;
  title: string;
  children: React.ReactNode;
  buttonText: string;
  buttonHref: string;
}> = ({ imageUrl, title, children, buttonText, buttonHref }) => (
  <Box position="relative" p={{ base: "40px 0 30px 0", lg: "100px 0" }}>
    <Box
      position="absolute"
      width="100vw"
      height="100%"
      top="0"
      left="50%"
      transform="translateX(-50%)"
      zIndex="-1"
      bgImage={`url(${imageUrl})`}
      bgSize="cover"
      bgPosition="center"
      display={{ base: "none", lg: "block" }}
    />
    <Flex
      maxW="1200px"
      mx="auto"
      direction={{ base: "column", lg: "row" }}
      justifyContent={{ base: "center", lg: "end" }}
      gap={{ base: "15px", lg: "50px" }}
      padding={{ base: "15px", lg: "0 15px" }}
    >
      <Box
        bg="var(--color-green)"
        flex={{ lg: "0 0 50%" }}
        maxW={{ lg: "50%" }}
        p={{ base: "30px", lg: "50px" }}
        color="white"
      >
        <Flex direction="column" gap="20px">
          <Heading
            fontSize={{ base: "30px", lg: "34px" }}
            lineHeight={{ base: "37px", lg: "48px" }}
            fontWeight="600"
          >
            {title}
          </Heading>
          {children}
          <Box pt="16px">
            <Button
              as="a"
              href={buttonHref}
              bg="black"
              color="white"
              borderRadius="0"
              py="20px"
              minH="50px"
              px="30px"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap="2"
              w="fit-content"
              _hover={{
                bg: 'white',
                color: 'black',
              }}
              transition=".3s"
              rightIcon={
                <Icon viewBox="0 0 9 14" w="9px" h="14px">
                  <path d="M0 13.6H2.70588L8.4 6.98565L2.70588 0.400024H0L5.69412 6.98565L0 13.6Z" fill="currentColor" />
                </Icon>
              }
            >
              {buttonText}
            </Button>
          </Box>
        </Flex>
      </Box>
    </Flex>
  </Box>
);

const TabContent: React.FC<{
  data: IntegrationData;
  isLargerThan757: boolean;
  menuItems: Array<{ title: string; href: string; }>;
}> = ({ data, isLargerThan757, menuItems }) => (
  <Grid
    templateColumns={{ base: "1fr", md: "minmax(auto, 370px) auto" }}
    gap={{ base: "0", md: "30px" }}
    p={{ 
      base: "0",
      md: "50px 15px", 
      lg: "50px 15px 50px" 
    }}
  >
    <Box>
      <Flex direction="column" gap="15px" p={{ base: "0", md: "15px 0" }}>
        {isLargerThan757 && (
        <>
             <Heading as="h2" color="var(--color-green)" fontSize="34px" lineHeight="48px" fontWeight="600">
                {data.title}
            </Heading>
            <Text fontSize="18px" lineHeight="24px" fontWeight="400">
          {data.subtitle}
            </Text>
            <Text fontWeight="400" fontSize={{ base: "12px", lg: "12px" }} lineHeight={{ base: "16px", lg: "19px" }}>
            {data.description}
            </Text>
        </>
        )}
        
        {isLargerThan757 && (
          <Flex direction="column" gap="15px" pt="30px">
            {menuItems.map((item) => (
              <Link
                key={item.title}
                href={item.href}
                color="black"
                fontSize="16px"
                lineHeight="21px"
                fontWeight="600"
                textDecoration="underline"
                w="max-content"
                _hover={{ color: "#1AAC7A" }}
              >
                {item.title}
              </Link>
            ))}
          </Flex>
        )}
      </Flex>
    </Box>

    <Box width="100%" overflow="hidden" px="0px" display={{ base: 'flex', md: 'block' }} flexDirection={{ base: 'column'}}>

      <Box 
        display={{ base: 'block', md: 'none' }}
        p="25px 15px 5px"
        order={{ base: 2}}
      >
        <Text
          fontSize="21px"
          lineHeight="100%"
          color="#171717"
          fontWeight="600"
          textAlign="center"
        >
          Zintegrowane systemy płatności
        </Text>
      </Box>
        
      <Grid
        templateColumns={{
          base: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          md: "repeat(2, 1fr)",
          lg: "repeat(3, 1fr)",
          xl: "repeat(4, 1fr)"
        }}
        autoRows="1fr"
        gap={{ base: "15px", lg: "30px" }}
        justifyContent="center"
        p={{base: "15px", md: "15px 0"}}
        order={{ base: 3}}
      >
        {data.items.map((item) => (
          <MarketplaceTile
            key={item.title}
            href={item.href || "#"}
            title={item.title}
            imageSrc={item.imageSrc}
          />
        ))}
      </Grid>
      
      <Box 
        maxW="1200px" 
        mx="auto"
        p={{ base: "30px 15px 0px", md: "35px 0px 0px" }}
        order={{ base: 1}}
      >
        <Box
          border="3px solid var(--color-border-gray)"
          p={{ base: "35px 30px", lg: "50px" }}
        >
          <Flex 
            direction="column"
            gap={{ base: "30px", lg: "30px" }}
            align="start"
          >
            <Box flex="0 0 auto">
              <Image 
                src="https://www.idosell.com/panel/file-gate.php?id=610800&ext=.svg" 
                alt="PCI DSS Certified"
                maxW="170px"
              />
            </Box>
            <Box flex="1">
              <Heading
                fontSize={{ base: "21px", lg: "21px" }}
                fontWeight="600"
                mb="15px"
              >
                IdoSell z certyfikatem bezpieczeństwa PCI DSS level 1
              </Heading>
              <Text
                fontSize={{ base: "12px", lg: "12px" }}
                mb="20px"
              >
                IdoSell jako jedyny dostawca systemu sklepów internetowych w Polsce posiada certyfikat posiadający najwyższy poziom bezpieczeństwa PCI DSS level 1, wymagany np. przez Visa i MasterCard do przechowywania numerów kart płatniczych.
              </Text>
              <Button
                as="a"
                href="https://www.idosell.com/pl/bezpieczny-sklep-internetowy-idosell-z-sla-na-poziomie-99-98-/pci-dss-czyli-bezpieczne-przetwarzanie-danych-posiadaczy-kart-platniczych/"
                variant="outline"
                color="#171717"
                border="2px solid #171717"
                borderRadius="0"
                py="20px"
                minH="50px"
                width={{base: "100%", lg: "auto"}}
                px="30px"
                _hover={{
                  bg: 'black',
                  color: 'white',
                }}
                rightIcon={
                  <Icon viewBox="0 0 9 14" w="9px" h="14px">
                    <path d="M0 13.6H2.70588L8.4 6.98565L2.70588 0.400024H0L5.69412 6.98565L0 13.6Z" fill="currentColor" />
                  </Icon>
                }
              >
                Dowiedz się więcej
              </Button>
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  </Grid>
);

const FeatureListItem: React.FC<FeatureListItemProps> = ({ title, description }) => (
  <Flex mb="30px" display="flex" flexDirection="row" gap="30px">
    <Icon 
      viewBox="0 0 24 24" 
      w="24px" 
      h="24px" 
      color={{ base: "#171717", lg: "var(--color-green)" }}
    >
      <path 
        d="M9.55 18.575L3.225 12.25L5.65 10.825L9.55 14.725L18.35 4.925L20.775 7.35L9.55 18.575Z" 
        strokeWidth="2"
        fill="currentColor"
      />
    </Icon>
    <Flex align="start" flexDirection="column">
      <Heading 
        as="h3" 
        fontSize="21px"
        lineHeight="1.4"
        mb={{base: "4px", lg: "8px"}}
      >
        {title}
      </Heading>
      <Text fontSize="16px" lineHeight="24px" fontWeight="400">
        {description}
      </Text>
    </Flex>
  </Flex>
);

const PaymentsPage: React.FC = () => {
  const [isLargerThan757] = useMediaQuery('(min-width: 757px)');
  const [isLargerThan979] = useMediaQuery('(min-width: 979px)');

  const marketplaceData: IntegrationData = {
    title: "Płatności",
    subtitle: "Wszystkie popularne metody płatności w jednym miejscu",
    description: "Sklepu internetowe IdoSell mają do dyspozycji wiele zintegrowanych systemów płatności. Duży wybór pozwala na trafne dobranie oferowanych form płatności w sklepach oraz wygodne zarządzanie wpłatami z różnych źródeł.",
    items: [
      { title: "eservice", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610768&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/system-platnosci-eservice-dla-sklepow-internetowych-idosell/" },
      { title: "first-data", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610777&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/integracja-sklepu-internetowego-z-systemem-platnosci-firstdata-polcard/" },
      { title: "PayPal", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610790&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/sklep-internetowy-idosell-z-platnosciami-paypal/" },
      { title: "PayU", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610791&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-idosell/integracja-sklepu-internetowego-z-systemem-platnosci-payu/" },
      { title: "Przelewy24", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610792&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/platnosci-przelewy24-w-sklepie-internetowym/" },
      { title: "Tpay", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610793&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/system-platnosci-internetowych-tpay/" },
      { title: "iMoje", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610794&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/imoje-ing-obsluzy-platnosci-w-pln-w-twoim-sklepie-internetowym/" },
      { title: "Klarna", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610795&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/klarna/" },
      { title: "leaselink", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610796&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/systemy-ratalne-obslugiwane-w-idosell/integracja-z-leaselink-jedynym-dedykowanym-rozwiazaniem-dla-b2b-w-zakresie-oferowania-leasingu-sklepom-internetowym/" },
      { title: "ca-raty", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610797&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/systemy-ratalne-obslugiwane-w-idosell/zaoferuj-swoim-klientom-raty-banku-credit-agricole/" },
      { title: "santander e-raty", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610798&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/systemy-ratalne-obslugiwane-w-idosell/integracja-z-wygodnym-systemem-ratalnym-eraty/" },
      { title: "pkobp leasing", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610799&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/systemy-platnosci-obslugiwane-w-sklepie-internetowym-idosell/systemy-ratalne-obslugiwane-w-idosell/pko-leasing-latwa-integracja-i-natychmiastowa-decyzja-o-leasingu-juz-w-trakcie-skladania-wniosku/" }
    ]
  };

  const menuItems = [
    { href: "https://www.idosell.com/pl/marketplaces/", title: "Marketplaces" },
    { href: "https://www.idosell.com/pl/kurierzy/", title: "Kurierzy" },
    { href: "https://www.idosell.com/pl/systemy-platnosci-obslugiwane-w-idosell/", title: "Płatności" },
    { href: "https://www.idosell.com/pl/integracja-z-hurtowniami/", title: "Hurtownie" },
    { href: "https://www.idosell.com/pl/systemy-erp/", title: "Systemy ERP" },
    { href: "https://www.idosell.com/pl/mozliwosci-systemu-wms-w-sklepach-internetowych/obsluga-fulfillment/", title: "Fullfilment" },
    { href: "https://www.idosell.com/pl/marketing/", title: "Marketing" },
    { href: "https://www.idosell.com/pl/integracje/", title: "Inne" },
  ];

  return (
    <Box
      className="payments__page"
      maxW="1200px"
      mx="auto"
      fontFamily="'Poppins', Arial, sans-serif"
      fontStyle="normal"
      color="var(--color-dark-text)"
      sx={{
        '--color-green': '#1AAC7A',
        '--color-dark-text': '#171717',
        '--color-tile-gray': '#eee',
        '--color-border-gray': '#C8C8C8',
      }}
    >
      {/* Hero Section */}
      <Box 
        position="relative"
        display="flex"
        justifyContent="center"
        mb={{base: "0", lg: "50px"}}
        _before={{
          content: '""',
          position: 'absolute',
          backgroundColor: 'var(--color-green)',
          width: '100vw',
          height: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: -1,
        }}
      >
        <Flex
          maxW="1200px"
          direction={{ base: 'column', lg: 'row-reverse' }}
          align="center"
          w="100%"
        >
          <Box 
            flex={{ lg: '1 0 50%' }}
            display="flex"
            justifyContent="center"
            bg="var(--color-green)"
          >
            <picture>
              <source media="(min-width: 979px)" srcSet="https://www.idosell.com/panel/file-gate.php?id=610842&ext=.png" />
              <source media="(min-width: 757px)" srcSet="https://www.idosell.com/panel/file-gate.php?id=610842&ext=.png" />
              <Image 
                src="https://www.idosell.com/panel/file-gate.php?id=610843&ext=.png" 
                alt="Mężczyzna płacący przez telefon"
                w={{ base: '100vw', lg: 'auto' }}
                maxW={{ base: '100vw', lg: '100%' }}
                maxH={{ lg: '485px', xl: '585px' }}
              />
            </picture>
          </Box>

          <Flex
            flex={{ lg: '1 0 50%' }}
            direction="column"
            gap="15px"
            p={{ base: '30px 20px', lg: '10px 50px 15px 15px' }}
            bg="var(--color-green)"
            color="white"
          >
            <Heading
              as="h1"
              fontSize={{ base: '36px', lg: '36px', xl: '54px' }}
              lineHeight={{ base: '42px', lg: '48px', xl: '69px' }}
              textAlign={{ base: 'center', lg: 'left' }}
              fontWeight="600"
              m="0"
            >
              Bezpieczne i szybkie płatności w Twoim e-sklepie
            </Heading>
            <Text
              fontSize={{ base: '16px', lg: '18px' }}
              lineHeight={{ base: '24px', lg: 'normal' }}
              textAlign={{ base: 'center', lg: 'left' }}
            >
              Oferuj klientom szybkie i wygodne płatności, które zwiększają konwersję i poprawiają doświadczenie zakupowe. 
            </Text>
          </Flex>
        </Flex>
      </Box>

      {/* Features Section */}
      <Box p={{ base: "50px 15px", lg: "50px 15px 30px" }}>
        <Heading
          textAlign="center"
          fontSize={{ base: "30px", lg: "34px" }}
          lineHeight={{ base: "1.2", lg: "48px" }}
          fontWeight="600"
          mb={{ base: "15px", lg: "12px" }}
        >
          IdoPay to wszystkie istotne metody płatności 
        </Heading>
        <Text
          textAlign="center"
          fontSize={{ base: "16px", lg: "18px" }}
          lineHeight="28px"
          fontWeight="400"
          mb={{ base: "30px", lg: "50px" }}
        >
          Płatności IdoPay to ogromne udogodnienie dla naszych sprzedawców. Dzięki wbudowanej opcji integracji, bez potrzeby podpisywania dodatkowej umowy, można obsługiwać wszystkie polskie banki. Co więcej, płatności księgowane są natychmiast z najniższą prowizją zaczynającą się od 1.2%!
        </Text>
        <Grid
          templateColumns={{
            base: "repeat(3, 1fr)",
            lg: "repeat(6, 1fr)"
          }}
          gap={{ base: "15px", lg: "30px" }}
          maxW="1200px"
          mx="auto"
          mb={{base: "10px", lg: "50px"}}
        >
          <FeatureTile
            imageSrc="https://www.idosell.com/panel/file-gate.php?id=610844&ext=.svg"
            alt="Visa"
          />
          <FeatureTile
            imageSrc="https://www.idosell.com/panel/file-gate.php?id=610845&ext=.svg"
            alt="MasterCard"
          />
          <FeatureTile
            imageSrc="https://www.idosell.com/panel/file-gate.php?id=610846&ext=.svg"
            alt="GPay"
          />
          <FeatureTile
            imageSrc="https://www.idosell.com/panel/file-gate.php?id=610847&ext=.svg"
            alt="Blik"
          />
          <FeatureTile
            imageSrc="https://www.idosell.com/panel/file-gate.php?id=610848&ext=.svg"
            alt="PayPo"
          />
          <FeatureTile
            imageSrc="https://www.idosell.com/panel/file-gate.php?id=610850&ext=.svg"
            alt="ApplePay"
          />
        </Grid>
      </Box>

      {/* Features List Section */}
      <Box 
        maxW="1200px" 
        mx="auto" 
        mb={{base: "0", lg: "50px"}}
        display="flex"
        gap={{ base: "30px", lg: "80px" }}
        flexDirection={{ base: "column", lg: "row" }}
      >
        <Box 
          flex="1"
          display={{ base: "none", lg: "block" }}
        >
          <Image 
            src="https://www.idosell.com/panel/file-gate.php?id=611788&ext=.png" 
            alt="IdoPay interface preview" 
            w="auto"
          />
        </Box>
        <Box 
          flex="1"
          px={{ base: "55px", lg: "0" }}
        >
          <FeatureListItem
            title="Płatności kartowe Visa i Mastercard"
            description="Wielowalutowe płatności kartami Visa i Mastercard"
          />
          <FeatureListItem
            title="BLIK i PayByLinks"
            description="Najpopularniejsze w Polsce płatności BLIK i szybkie przelewy PayByLinks"
          />
          <FeatureListItem
            title="Apple Pay i Google Pay"
            description="Płatności portfelami elektronicznymi Apple Pay oraz Google Pay bez konieczności podawania danych kart płatniczych"
          />
          <FeatureListItem
            title="Rozliczenia walut"
            description="Sprzedawaj bez granic i przyjmuj płatności w różnych walutach"
          />
          <FeatureListItem
            title="Płatności odroczone (PayPo, Klarna)"
            description="Klienci kupują teraz, a płacą później, co zwiększa wartość koszyków"
          />

          <Link 
            href="https://www.idosell.com/pl/idopay/"
            display={{base: "none", lg: "inline-flex"}}
            alignItems="center"
            bg="black"
            color="white"
            fontWeight="600"
            py="12px"
            px="30px"
            mt="5px"
            _hover={{
              bg: 'var(--color-green)',
              textDecoration: 'none'
            }}
            transition=".3s"
          >
            Dowiedz się więcej o IdoPay
            <Icon 
              viewBox="0 0 9 14" 
              w="9px" 
              h="14px" 
              ml="10px"
            >
              <path 
                d="M0 13.6H2.70588L8.4 6.98565L2.70588 0.400024H0L5.69412 6.98565L0 13.6Z" 
                fill="currentColor" 
              />
            </Icon>
          </Link>
        </Box>
      </Box>

      {/* Marketplace Section */}
      <Box maxW="1200px" mx="auto">
        <TabContent 
          data={marketplaceData}
          isLargerThan757={isLargerThan757}
          menuItems={menuItems}
        />
      </Box>

      {/* Banner Section */}
      {isLargerThan757 && (
        <CustomSection
          imageUrl="https://www.idosell.com/panel/file-gate.php?id=610492&ext=.jpeg"
          title="Gotowe i zawsze aktualne integracje w IdoSell"
          buttonText="Dowiedz się więcej"
          buttonHref="https://www.idosell.com/pl/integracje/"
        >
          <Text fontWeight="600" fontSize="26px" lineHeight="34px" color="#171717">
            W IdoSell nie musisz szukać integracji na własną rękę ani martwić się o ich aktualizację. 
          </Text>
          <Text fontWeight="400" fontSize="16px" lineHeight="25px" color="#171717">
            Korzystaj z gotowych i wbudowanych połączeń z systemami płatności, kurierami, marketplace'ami, porównywarkami cen i serwisami opinii. Wszystko działa od ręki i bez dodatkowej konfiguracji.
          </Text>
        </CustomSection>
      )}

      {/* Contact Section */}
      <Flex
        direction="column"
        align="center"
        textAlign="center"
        p={{base: "90px 15px", lg: "55px 20px"}}
      >
                                <Heading
          as="h2"
          mb="15px"
          fontSize={{ base: "30px", lg: "34px" }}
          lineHeight={{ base: "37px", lg: "48px" }}
          fontWeight="600"
        >
          Brakuje integracji, której szukasz?
                                </Heading>
        <Text
          mb="30px"
          fontSize={{ base: "16px", lg: "18px" }}
        >
          Możemy dla Ciebie zrobić integrację na życzenie.
                                </Text>
        <Button
          as="a"
          href="https://csc.idosell.com/support/contact_form/597?title=Prace%20kontraktowe"
          maxW="350px"
          w="100%"
          bg="black"
          color="white"
          borderRadius="0"
          py="20px"
          minH="50px"
          px="30px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="2"
          _hover={{
            bg: 'var(--color-green)',
            color: 'white',
          }}
          transition=".3s"
          rightIcon={
            <Icon viewBox="0 0 9 14" w="9px" h="14px">
              <path d="M0 13.6H2.70588L8.4 6.98565L2.70588 0.400024H0L5.69412 6.98565L0 13.6Z" fill="currentColor" />
            </Icon>
          }
        >
          Przejdź do biura obsługi klienta
        </Button>
      </Flex>
                </Box>
    );
};

export default PaymentsPage;